import { Injectable } from '@angular/core'
import { FilterGroup, FilterGroupFilters } from '@helios/common-api'
import {
  RootState,
  createFilterGroup,
  deleteFilterGroup,
  selectFilterGroupsByPrefix,
  setFilterGroupsByPrefix,
  updateFilterGroup,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { take } from 'rxjs/operators'
import { v4 as UUID } from 'uuid'
import { FilterGroupService, FilterGroupUpdate } from '../filter-group.service'
import { UiStorageService } from '../ui-storage.service'

export const FILTER_GROUP_PREFIX = `filtergroup`

@Injectable()
export class HttpFilterGroupService extends FilterGroupService {
  filterGroupAdditions: Subject<FilterGroup> = new Subject<FilterGroup>()

  private readonly userPrefix: Promise<string>

  constructor(
    private readonly storage: UiStorageService,
    private readonly store: Store<RootState>,
  ) {
    super()
    const base32User = storage.getBase32User()
    const clientId = storage.getClientId()
    this.userPrefix = Promise.all<string, number>([base32User, clientId]).then(
      userAndClient =>
        `${FILTER_GROUP_PREFIX}/${userAndClient[1]}/${userAndClient[0]}`,
    )
  }

  async getFilterGroups(gridKey: string): Promise<FilterGroup[]> {
    const gridPrefix = await this.getGridPrefix(gridKey)

    let filterGroups = await this.store
      .pipe(selectFilterGroupsByPrefix(gridPrefix), take(1))
      .toPromise()

    if (filterGroups == null) {
      const folderContents = await this.storage.getFolderContents(gridPrefix)
      filterGroups = []
      for (const filterGroupPrefix of folderContents.objectPrefixes) {
        const filterGroup = await this.getFilterGroup(filterGroupPrefix)
        if (filterGroup != null) filterGroups.push(filterGroup)
      }

      this.store.dispatch(
        setFilterGroupsByPrefix({
          [gridPrefix]: filterGroups,
        }),
      )
    }

    return filterGroups
  }

  getFilterGroup(filterGroupPrefix: string): Promise<FilterGroup | undefined> {
    return this.storage.getObject(filterGroupPrefix)
  }

  async updateFilterGroup(
    filterGroupPrefix: string,
    update: FilterGroupUpdate,
  ): Promise<void> {
    const group = await this.getFilterGroup(filterGroupPrefix)
    if (group == null) return

    const updatedFilterGroup = { ...group, ...update }

    await this.storage
      .putObject(filterGroupPrefix, updatedFilterGroup)
      .then(() => {
        this.store.dispatch(
          updateFilterGroup({
            gridPrefix: this.splitGridId(filterGroupPrefix)[0],
            filterGroup: updatedFilterGroup,
          }),
        )
      })
  }

  async createFilterGroup(
    gridKey: string,
    name: string,
    description: string,
    filters: FilterGroupFilters,
  ): Promise<string> {
    const newFilterGroupPrefix = await this.getNewFilterGroupPrefix(gridKey)
    const newFilterGroup: FilterGroup = {
      gridKey,
      name,
      description,
      filters,
      filterGroupPrefix: newFilterGroupPrefix,
    }
    await this.storage.putObject(newFilterGroupPrefix, newFilterGroup)

    this.filterGroupAdditions.next(newFilterGroup)

    const gridPrefix = await this.getGridPrefix(gridKey)
    this.store.dispatch(
      createFilterGroup({
        gridPrefix: gridPrefix,
        filterGroup: newFilterGroup,
      }),
    )

    return newFilterGroupPrefix
  }

  async cloneFilterGroup(
    filterGroupPrefix: string,
    newName: string,
    newDescription: string,
  ): Promise<string | undefined> {
    const oldFilterGroup = await this.getFilterGroup(filterGroupPrefix)
    if (oldFilterGroup == null) return undefined

    const { gridKey, filters } = oldFilterGroup
    return this.createFilterGroup(gridKey, newName, newDescription, filters)
  }

  deleteFilterGroup(filterGroupPrefix: string): Promise<void> {
    return this.storage.deleteObject(filterGroupPrefix).then(() => {
      const [prefix, id] = this.splitGridId(filterGroupPrefix)

      this.store.dispatch(
        deleteFilterGroup({
          prefix,
          id,
        }),
      )
    })
  }

  private async getGridPrefix(gridKey: string): Promise<string> {
    const prefix = await this.userPrefix
    return `${prefix}/${gridKey}`
  }

  private splitGridId(gridId: string): string[] {
    const lastSlashIndex: number = gridId.lastIndexOf('/')

    const prefix = gridId.substring(0, lastSlashIndex)
    const key = gridId.substring(lastSlashIndex + 1)

    return [prefix, key]
  }

  private async getNewFilterGroupPrefix(gridKey: string): Promise<string> {
    const newFilterGroupId = UUID()
    const prefix = await this.getGridPrefix(gridKey)
    return `${prefix}/${newFilterGroupId}`
  }
}
